import React from 'react';
import { Box } from '@match/react-component-library';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import styled from 'packages/theming';
import { useLocale } from 'modules/hooks';
import { Quote } from '../../components';
import { MEDIA_QUERY } from 'modules/constants/media-queries';
import leftPhotoSrc from './bottom-left-couple.jpeg';
import rightPhotoSrc from './bottom-right-couple.jpeg';

const OurTimeSplashPhotos = () => {
  const intl = useIntl();
  const photoAlt = intl.formatMessage(messages.successPhotoAlt);
  const { locale } = useLocale();

  const PhotosAndQuotes = () => (
    <React.Fragment>
      <PhotoAndQuoteBox>
        <LeftPhoto src={leftPhotoSrc} alt={photoAlt} />
        <BottomLeftQuoteBox>
          <Quote couplesName={messages.bottomLeftQuoteCoupleName.defaultMessage} locale={locale}>
            <FormattedMessage {...messages.bottomLeftQuote} />
          </Quote>
        </BottomLeftQuoteBox>
      </PhotoAndQuoteBox>
      <PhotoAndQuoteBox>
        <RightPhoto src={rightPhotoSrc} alt={photoAlt} />
        <BottomRightQuoteBox>
          <Quote couplesName={messages.bottomRightQuoteCoupleName.defaultMessage} locale={locale}>
            <FormattedMessage {...messages.bottomRightQuote} />
          </Quote>
        </BottomRightQuoteBox>
      </PhotoAndQuoteBox>
    </React.Fragment>
  );

  return (
    <SplashImagesBox width={556} height={635} position="relative" marginX="auto">
      <PhotosAndQuotes />
    </SplashImagesBox>
  );
};

const SplashImagesBox = styled(Box)(({ theme }) => ({
  [MEDIA_QUERY.MEDIUM_DEVICE]: {
    width: 513,
    height: 516,
  },
  [MEDIA_QUERY.TABLET]: {
    width: '55%',
    marginRight: theme.space.none,
    height: 400,
  },
  [MEDIA_QUERY.MOBILE]: {
    width: '100%',
    height: 'auto',
  },
}));

const PhotoAndQuoteBox = styled(Box)({
  [MEDIA_QUERY.MOBILE]: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    marginBottom: '82px',
    alignItems: 'center',
  },
});

const Photo = styled('img')(({ theme }) => ({
  borderRadius: theme.radii.roundCorner2x,
  width: 266,
  height: 376,
  [MEDIA_QUERY.MEDIUM_DEVICE]: {
    width: 218,
    height: 300,
  },
  [MEDIA_QUERY.TABLET]: {
    width: 135,
    height: 190,
  },
  [MEDIA_QUERY.MOBILE]: {
    width: 118,
    height: 163,
    position: 'static',
    marginRight: theme.space.xxlarge,
  },
}));

const RightPhoto = styled(Photo)({
  position: 'absolute',
  right: '-1px',
  bottom: '-30px',
  objectFit: 'cover',
  zIndex: 1,
  [MEDIA_QUERY.MEDIUM_DEVICE]: {
    right: 32,
  },
  [MEDIA_QUERY.TABLET]: {
    right: '140px',
    bottom: '-50px',
  },
  [MEDIA_QUERY.MOBILE]: {
    right: 92,
    bottom: 17,
  },
});

const LeftPhoto = styled(Photo)(({ theme }) => ({
  position: 'absolute',
  left: '2px',
  bottom: '-30px',
  objectFit: 'cover',
  zIndex: 3,
  [MEDIA_QUERY.MOBILE]: {
    left: '225px',
    marginLeft: theme.space.xxlarge,
    marginRight: theme.space.none,
    order: 2,
  },
  [MEDIA_QUERY.MEDIUM_DEVICE]: {
    left: '24px',
  },
  [MEDIA_QUERY.TABLET]: {
    bottom: '-50px',
    left: 'initial',
    right: '295px',
  },
}));

const BottomLeftQuoteBox = styled(Box)(() => ({
  bottom: '-112px',
  left: '-194px',
  position: 'absolute',
  width: '188px',
  [MEDIA_QUERY.MEDIUM_DEVICE]: {
    bottom: '-120px',
    left: '-170px',
  },
  [MEDIA_QUERY.TABLET]: {
    left: 'initial',
    right: '440px',
    bottom: '-205px',
  },
  [MEDIA_QUERY.MOBILE]: {
    display: 'block',
    position: 'relative',
    width: '100%',
    right: '0px',
    bottom: '0px',
  },
}));

const BottomRightQuoteBox = styled(Box)(() => ({
  textAlign: 'right',
  bottom: '360px',
  right: '-13px',
  position: 'absolute',
  width: '370px',
  [MEDIA_QUERY.MEDIUM_DEVICE]: {
    bottom: '285px',
  },
  [MEDIA_QUERY.TABLET]: {
    width: '320px',
    bottom: '150px',
    right: '65px',
  },
  [MEDIA_QUERY.MOBILE]: {
    textAlign: 'left',
    position: 'relative',
    display: 'block',
    width: '100%',
    right: '0px',
    bottom: '0px',
  },
}));

const messages = defineMessages({
  successPhotoAlt: {
    id:
      'src/modules/components/Splash/components/OurTimeSplashPhotos/OurTimeSplashPhotos:successPhotoAlt',
    description: 'photo of success story couple',
    defaultMessage: 'Success Couple',
  },
  bottomLeftQuote: {
    id:
      'src/modules/components/Splash/components/OurTimeSplashPhotos/OurTimeSplashPhotos:bottomLeftQuote',
    description: 'user testimonial quote',
    defaultMessage:
      "“I don't know how I could have met someone with all of Brian's qualifications, and the happiness he has brought me without Ourtime. I think I've met all of the frogs in the pond with none of them as stellar as my guy.”",
  },
  bottomLeftQuoteCoupleName: {
    id:
      'src/modules/components/Splash/components/OurTimeSplashPhotos/OurTimeSplashPhotos:bottomLeftQuoteCoupleName',
    description: 'user testimonial quote couple name',
    defaultMessage: 'Brian and JoAnn',
  },
  bottomRightQuote: {
    id:
      'src/modules/components/Splash/components/OurTimeSplashPhotos/OurTimeSplashPhotos:bottomRightQuote',
    description: 'user testimonial quote',
    defaultMessage:
      '“We have really found our soul mates and we would like to let everyone know that there is a match for you out there...you just have to keep trying until you find the right one!”',
  },
  bottomRightQuoteCoupleName: {
    id:
      'src/modules/components/Splash/components/OurTimeSplashPhotos/OurTimeSplashPhotos:bottomRightQuoteCoupleName',
    description: 'user testimonial quote couple name',
    defaultMessage: 'Tom and Fran',
  },
});

export default OurTimeSplashPhotos;
